/**
 * Upright 100%-stacked columns, one per category — the Ledger's `stackChart`.
 *
 * Each column is normalised to its own total. Segments stack bottom-up in the order
 * given, with a 2px gap; any segment over 13% of its column carries its share. A
 * held category gets the accent highlight behind it and the rest dim.
 */
import { useEffect, useState } from 'react'
import { FloatingTip } from '@/components/Charts'
import { int } from '@/format'

export interface StackSeries {
  key: string
  label: string
  color: string
  /** Label colour on the fill; per step, because dark mode flips the ramp. */
  fg: string
  values: number[]
}

export function StackColumns({
  categories,
  series,
  height = 290,
  selected = null,
  onSelect,
  partial = [],
}: {
  categories: { key: string; label: string }[]
  series: StackSeries[]
  height?: number
  selected?: string | null
  onSelect?: (key: string) => void
  /** Category keys whose period had not closed; the tip says "(partial)". */
  partial?: string[]
}) {
  const [hover, setHover] = useState<{ i: number; cx: number; cy: number } | null>(null)

  // A fixed tip does not scroll with the page; hide it rather than let it drift.
  useEffect(() => {
    if (!hover) return
    const off = () => setHover(null)
    window.addEventListener('scroll', off, true)
    return () => window.removeEventListener('scroll', off, true)
  }, [hover])

  const W = 760
  const H = height
  const mL = 44
  const mR = 14
  const mT = 12
  const mB = 30
  const n = categories.length
  const slot = n ? (W - mL - mR) / n : 0
  const bw = Math.min(58, slot - 12)
  const plotH = H - mT - mB
  const y = (share: number) => mT + plotH * (1 - share)
  const totals = categories.map((_, i) => series.reduce((a, s) => a + (s.values[i] ?? 0), 0))

  const hc = hover ? categories[hover.i] : undefined
  const pct1 = (v: number) => `${(v * 100).toFixed(1)}%`

  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ overflow: 'visible', display: 'block' }} role="img">
        {[0, 0.25, 0.5, 0.75, 1].map((t) => (
          <g key={t}>
            <line
              x1={mL}
              x2={W - mR}
              y1={y(t)}
              y2={y(t)}
              stroke={t === 0 ? 'var(--rule-2)' : 'var(--rule)'}
              strokeWidth={1}
            />
            <text
              x={mL - 8}
              y={y(t) + 3.5}
              textAnchor="end"
              fontSize={10}
              fill="var(--ink-3)"
              fontFamily="var(--mono)"
            >
              {Math.round(t * 100)}%
            </text>
          </g>
        ))}
        {categories.map((c, i) => {
          const cx = mL + slot * i + slot / 2
          const x = cx - bw / 2
          const tot = totals[i] ?? 0
          const on = selected === c.key
          const dim = selected != null && !on
          let acc = 0
          return (
            <g
              key={c.key}
              style={{ cursor: onSelect ? 'pointer' : 'default' }}
              onMouseMove={(e) => setHover({ i, cx: e.clientX, cy: e.clientY })}
              onMouseLeave={() => setHover(null)}
              onClick={
                onSelect
                  ? () => {
                      setHover(null)
                      onSelect(c.key)
                    }
                  : undefined
              }
            >
              <rect x={cx - slot / 2} y={0} width={slot} height={H} fill="transparent" />
              {on && (
                <rect
                  x={x - 5}
                  y={mT - 4}
                  width={bw + 10}
                  height={plotH + 8}
                  rx={5}
                  fill="var(--accent-soft)"
                  stroke="var(--accent)"
                  strokeWidth={1}
                />
              )}
              <g opacity={dim ? 0.32 : 1}>
                {tot > 0 &&
                  series.map((s) => {
                    const share = (s.values[i] ?? 0) / tot
                    if (share <= 0) return null
                    const top = y(acc + share)
                    const h = Math.max(0, y(acc) - top - 2)
                    acc += share
                    return (
                      <g key={s.key}>
                        <rect x={x} y={top} width={bw} height={h} rx={2} fill={s.color} />
                        {share > 0.13 && (
                          <text
                            x={cx}
                            y={top + h / 2 + 3.5}
                            textAnchor="middle"
                            fontSize={10}
                            fontWeight={600}
                            fontFamily="var(--mono)"
                            fill={s.fg}
                          >
                            {Math.round(share * 100)}%
                          </text>
                        )}
                      </g>
                    )
                  })}
              </g>
              <text
                x={cx}
                y={H - mB + 16}
                textAnchor="middle"
                fontSize={10}
                fontFamily="var(--mono)"
                fill="var(--ink-2)"
                opacity={dim ? 0.4 : 1}
              >
                {c.label}
              </text>
            </g>
          )
        })}
      </svg>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px 14px', margin: '10px 0 2px' }}>
        {series.map((s) => (
          <span
            key={s.key}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--ink-2)' }}
          >
            <i style={{ width: 11, height: 11, borderRadius: 3, background: s.color, flex: 'none' }} />
            {s.label}
          </span>
        ))}
      </div>

      {hover && hc && (
        <FloatingTip x={hover.cx} y={hover.cy}>
          <div className="chart-tip-title">
            {hc.label}
            {partial.includes(hc.key) && (
              <span style={{ color: 'var(--ink-3)', fontSize: 11, fontWeight: 400 }}> (partial)</span>
            )}
          </div>
          <div className="chart-tip-row">
            <span>Resolved</span>
            <span>{int(totals[hover.i] ?? 0)}</span>
          </div>
          {series.map((s) => {
            const v = s.values[hover.i] ?? 0
            const tot = totals[hover.i] ?? 0
            return (
              <div key={s.key} className="chart-tip-row">
                <span>
                  <i className="chart-tip-swatch" style={{ background: s.color }} />
                  {s.label}
                </span>
                <span>
                  {tot ? pct1(v / tot) : '—'} · {int(v)}
                </span>
              </div>
            )
          })}
          {onSelect && (
            <div style={{ color: 'var(--ink-3)', fontSize: 11, marginTop: 5 }}>
              {selected === hc.key
                ? 'Click to clear'
                : `Click to hold the tab to ${hc.label}`}
            </div>
          )}
        </FloatingTip>
      )}
    </>
  )
}
